interface Asset {
  id: number;
  asset_id: number | null;
  product_name: string;
  product_model: string;
  product_brand: string;
  product_image: string;
  serial_no: string;
  overall_status: "assigned" | "in_stock" | "idle" | "lost";
  asset_exists: boolean;
  assigned_to_type: string;
  assigned_to_name: string;
  assigned_by_name: string;
}

interface AssetHistoryItem {
  id: number;
  asset_id: number;
  action_type: string;
  created_at: string;
  notes?: string;
  action_details?: any;
}

interface AssetChangeStats {
  active: number;
  inactive: number;
  updated: number;
  total: number;
  changedAssetsList: ChangedAsset[];
}

interface ChangedAsset {
  id: number;
  asset_id: number;
  product_name: string;
  product_model: string;
  product_brand: string;
  product_image: string;
  serial_no: string;
  assigned_to_name: string;
  assigned_to_type: string;
  changes_type: "active" | "inactive" | "updated";
  total_changes: number;
  status: string;
}

export const calculateAssetChanges = (
  assets: Asset[],
  assetHistory: AssetHistoryItem[]
): AssetChangeStats => {
  const assetMap = new Map<number, Asset>();
  assets.forEach((asset) => {
    if (asset.asset_exists && asset.asset_id) {
      assetMap.set(asset.asset_id, asset);
    }
  });

  const historyMap = new Map<number, AssetHistoryItem[]>();
  assetHistory.forEach((history) => {
    if (!historyMap.has(history.asset_id)) {
      historyMap.set(history.asset_id, []);
    }
    historyMap.get(history.asset_id)!.push(history);
  });

  historyMap.forEach((histories) => {
    histories.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  });

  const changedAssetsList: ChangedAsset[] = [];
  const stats = {
    active: 0,
    inactive: 0,
    updated: 0,
    total: 0,
  };

  historyMap.forEach((histories, assetId) => {
    const asset = assetMap.get(assetId);
    if (!asset) return;

    const latestHistory = histories[0];
    const changeType = getChangeType(latestHistory.action_type);
    const totalChanges = histories.length;

    const changedAsset: ChangedAsset = {
      id: asset.id,
      asset_id: assetId,
      product_name: asset.product_name,
      product_model: asset.product_model,
      product_brand: asset.product_brand,
      product_image: asset.product_image,
      serial_no: asset.serial_no,
      assigned_to_name: asset.assigned_to_name,
      assigned_to_type: asset.assigned_to_type,
      changes_type: changeType,
      total_changes: totalChanges,
      status:
        asset.overall_status === "assigned"
          ? "Active"
          : asset.overall_status === "in_stock"
          ? "In Stock"
          : asset.overall_status === "lost"
          ? "Lost"
          : "Idle",
    };

    changedAssetsList.push(changedAsset);
    stats[changeType]++;
  });

  stats.total = changedAssetsList.length;

  return {
    ...stats,
    changedAssetsList: changedAssetsList.sort(
      (a, b) => b.total_changes - a.total_changes
    ),
  };
};

const getChangeType = (
  actionType: string
): "active" | "inactive" | "updated" => {
  const normalizedAction = actionType.toLowerCase();

  switch (normalizedAction) {
    case "assigned":
    case "reassigned":
    case "ownership_changed":
      return "active";
    case "unassigned":
    case "deleted":
    case "lost":
      return "inactive";
    case "updated":
    case "modified":
    case "recovered":
      return "updated";
    default:
      return "updated";
  }
};

export const getChangedAssetsByType = (
  assetChanges: AssetChangeStats,
  changeType: "active" | "inactive" | "updated"
): ChangedAsset[] => {
  return assetChanges.changedAssetsList.filter(
    (asset) => asset.changes_type === changeType
  );
};
